import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, CreditCard, CheckSquare, Dumbbell,
  UserCheck, Lock, Menu, X, Moon, Sun, TrendingUp, BarChart2, Settings, LogOut, HelpCircle, AlertTriangle, Shield, ArrowLeft, QrCode, CalendarClock
} from 'lucide-react';
import { differenceInDays, parseISO } from 'date-fns';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { getBusinessHoursSlot, formatHoursSlot } from '../utils/helpers';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: '대시보드' },
  { path: '/members', icon: Users, label: '회원 관리' },
  { path: '/memberships', icon: CreditCard, label: '회원권 관리' },
  { path: '/attendance', icon: CheckSquare, label: '출석 관리' },
  { path: '/pt', icon: Dumbbell, label: 'PT 관리' },
  { path: '/trainers', icon: UserCheck, label: '트레이너 관리' },
  { path: '/lockers', icon: Lock, label: '락커 관리' },
  { path: '/revenue', icon: TrendingUp, label: '매출 관리' },
  { path: '/analytics', icon: BarChart2, label: '통계/분석' },
  { path: '/help', icon: HelpCircle, label: '도움말' },
];

const pageTitles = {
  '/': '대시보드',
  '/members': '회원 관리',
  '/memberships': '회원권 관리',
  '/attendance': '출석 관리',
  '/pt': 'PT 관리',
  '/trainers': '트레이너 관리',
  '/lockers': '락커 관리',
  '/revenue': '매출 관리',
  '/analytics': '통계/분석',
  '/settings': '설정',
  '/help': '도움말',
};

const Layout = ({ children, basePath = '' }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { darkMode, setDarkMode, gymSettings, isAdminMode } = useApp();
  const { isDemoMode, signOut, gym } = useAuth();
  const navigate = useNavigate();

  const location = useLocation();

  const pageTitle = Object.entries(pageTitles).find(([path]) => {
    const full = basePath + path;
    return path === '/'
      ? location.pathname === basePath || location.pathname === basePath + '/'
      : location.pathname.startsWith(full);
  })?.[1] || '';

  const Sidebar = ({ mobile = false }) => (
    <nav className="flex flex-col h-full">
      {/* 로고 */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-gray-200 dark:border-gray-700">
        <div className="w-9 h-9 bg-primary-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <Dumbbell className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <div className="font-bold text-gray-900 dark:text-white text-sm truncate">
            {gymSettings?.name || 'GYM Manager'}
          </div>
          {(() => {
            const slot = getBusinessHoursSlot(gymSettings?.businessHours);
            const text = formatHoursSlot(slot);
            if (!text) return null;
            return (
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{text}</div>
            );
          })()}
        </div>
        {mobile && (
          <button onClick={() => setSidebarOpen(false)} className="ml-auto text-gray-400 flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* 네비게이션 */}
      <div className="flex-1 overflow-y-auto py-4 px-3">
        <ul className="space-y-1">
          {navItems.map(({ path, icon: Icon, label }) => (
            <li key={path}>
              <NavLink
                to={basePath + path}
                end={path === '/'}
                onClick={() => mobile && setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-gray-200'
                  }`
                }
              >
                <div className="relative flex-shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <span>{label}</span>
              </NavLink>
            </li>
          ))}
        </ul>

        {/* 키오스크 모드 버튼 */}
        {gym?.code && (
          <div className="mt-3 px-0">
            <button
              onClick={() => window.open(`/kiosk?code=${gym.code}`, '_blank')}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white transition-colors"
            >
              <QrCode className="w-5 h-5 flex-shrink-0" />
              <span>키오스크 모드</span>
            </button>
          </div>
        )}
      </div>

      {/* 하단: 플랜 상태 + 다크모드 + 설정 */}
      <div className="px-3 pb-4 border-t border-gray-200 dark:border-gray-700 pt-4 space-y-1">
        {/* 플랜 잔여 기간 표시 */}
        {!isDemoMode && !isAdminMode && gym?.plan_expires_at && (() => {
          const days = differenceInDays(parseISO(gym.plan_expires_at), new Date());
          const planLabel = gym.plan === 'trial' ? '무료체험' : '이용중';
          const color = days < 0 ? 'text-red-500 dark:text-red-400' : days <= 7 ? 'text-orange-500 dark:text-orange-400' : days <= 30 ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-400 dark:text-gray-500';
          return (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-700/50 mb-1">
              <CalendarClock className={`w-4 h-4 flex-shrink-0 ${color}`} />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-300">{planLabel}</p>
                <p className={`text-xs font-bold ${color}`}>
                  {days < 0 ? '만료됨' : days === 0 ? '오늘 만료' : `D-${days}`}
                </p>
              </div>
            </div>
          );
        })()}
        <NavLink
          to={basePath + '/settings'}
          onClick={() => mobile && setSidebarOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
              isActive
                ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-gray-200'
            }`
          }
        >
          <Settings className="w-5 h-5 flex-shrink-0" />
          <span>설정</span>
        </NavLink>
        <button
          onClick={() => setDarkMode(!darkMode)}
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
        >
          {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          {darkMode ? '라이트 모드' : '다크 모드'}
        </button>
        {!isDemoMode && (
          <button
            onClick={signOut}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            로그아웃
          </button>
        )}
      </div>
    </nav>
  );

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* 어드민 모드 배너 */}
      {isAdminMode && (
        <div className="flex items-center justify-between bg-red-600 text-white px-4 py-2 text-sm font-medium flex-shrink-0">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 flex-shrink-0" />
            <span>어드민 모드 — <span className="font-bold">{gymSettings?.name || '헬스장'}</span> 대신 관리 중</span>
          </div>
          <button
            onClick={() => navigate('/admin/gyms')}
            className="flex items-center gap-1 text-xs bg-red-700 hover:bg-red-800 px-3 py-1 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            어드민 콘솔로 돌아가기
          </button>
        </div>
      )}
      {/* 데모 모드 배너 */}
      {isDemoMode && (
        <div className="flex items-center justify-between bg-amber-400 text-amber-900 px-4 py-2 text-sm font-medium flex-shrink-0">
          <span>데모 모드로 실행 중 — 데이터는 이 브라우저에만 저장됩니다.</span>
          <button
            onClick={signOut}
            className="flex items-center gap-1 text-xs underline hover:no-underline ml-4"
          >
            <LogOut className="w-3.5 h-3.5" />
            데모 종료
          </button>
        </div>
      )}
      {/* 플랜 만료 임박 배너 */}
      {!isDemoMode && gym?.plan_expires_at && (() => {
        const days = differenceInDays(parseISO(gym.plan_expires_at), new Date());
        if (days < 0 || days > 7) return null;
        const isUrgent = days <= 3;
        return (
          <div className={`flex items-center gap-2 px-4 py-2 text-sm font-medium flex-shrink-0 ${isUrgent ? 'bg-red-500 text-white' : 'bg-orange-400 text-orange-900'}`}>
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>
              {days === 0 ? '오늘 플랜이 만료됩니다.' : `플랜이 ${days}일 후 만료됩니다.`} 관리자에게 갱신을 요청해주세요.
            </span>
          </div>
        );
      })()}
      <div className="flex flex-1 min-h-0 overflow-hidden">
      {/* 데스크탑 사이드바 */}
      <aside className="hidden lg:flex lg:flex-col w-60 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex-shrink-0">
        <Sidebar />
      </aside>

      {/* 모바일 사이드바 오버레이 */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-64 bg-white dark:bg-gray-800 flex flex-col">
            <Sidebar mobile />
          </aside>
        </div>
      )}

      {/* 메인 콘텐츠 */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* 헤더 */}
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 lg:px-6 py-4 flex items-center gap-4 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Menu className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">{pageTitle}</h1>
          {!isDemoMode && !isAdminMode && gym?.plan_expires_at && (() => {
            const days = differenceInDays(parseISO(gym.plan_expires_at), new Date());
            const planLabel = gym.plan === 'trial' ? '무료체험' : '이용중';
            const style = days < 0
              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
              : days <= 7
              ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
              : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
            return (
              <span className={`hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${style}`}>
                {planLabel} · {days < 0 ? '만료' : days === 0 ? 'D-Day' : `D-${days}`}
              </span>
            );
          })()}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </header>

        {/* 페이지 콘텐츠 */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
    </div>
  );
};

export default Layout;
